import "@/index.css";
import "@fontsource/manrope/400.css";
import "@fontsource/manrope/500.css";
import "@fontsource/manrope/600.css";
import "@fontsource/manrope/700.css";
import "@fontsource/manrope/800.css";
import "@fontsource/dm-sans/400.css";
import "@fontsource/dm-sans/500.css";
import "@fontsource/dm-sans/600.css";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef, createContext, useContext } from "react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import NewRoleplay from "@/pages/NewRoleplay";
import ActiveRoleplay from "@/pages/ActiveRoleplay";
import SessionResults from "@/pages/SessionResults";
import SharedResults from "@/pages/SharedResults";
import SharedCallAnalysis from "@/pages/SharedCallAnalysis";
import CallAnalysis from "@/pages/CallAnalysis";
import AnalysisResults from "@/pages/AnalysisResults";
import ScriptAnalysis from "@/pages/ScriptAnalysis";
import ScriptHistory from "@/pages/ScriptHistory";
import ScriptResults from "@/pages/ScriptResults";
import ScriptCompare from "@/pages/ScriptCompare";
import Leaderboard from "@/pages/Leaderboard";
import Analytics from "@/pages/Analytics";
import History from "@/pages/History";
import Settings from "@/pages/Settings";
import Team from "@/pages/Team";
import TeamMemberSession from "@/pages/TeamMemberSession";
import EmbeddedScenario from "@/pages/EmbeddedScenario";
import EmbeddedActiveRoleplay from "@/pages/EmbeddedActiveRoleplay";
import EmbeddedResults from "@/pages/EmbeddedResults";
import Embeds from "@/pages/Embeds";
import ObjectionHandler from "@/pages/ObjectionHandler";
import TeamsAdmin from "@/pages/TeamsAdmin";
import Layout from "@/components/Layout";

// API URL: always use REACT_APP_BACKEND_URL (ingress routes /api/* to backend)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API = `${BACKEND_URL}/api`;

// Theme Context
const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem("theme");
    return saved || "dark";
  });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === "dark" ? "light" : "dark");
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Auth Context
const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async (signal) => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: "include",
        signal
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Auth check failed:", error);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include"
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  useEffect(() => {
    const controller = new AbortController();
    checkAuth(controller.signal);
    return () => controller.abort();
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth: () => checkAuth() }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      
      // Using XMLHttpRequest instead of fetch to avoid "body stream already read" 
      // issues that occur specifically in production Kubernetes deployments
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API}/auth/session`, true);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.withCredentials = true;
      
      xhr.onload = function() {
        console.log("[AUTH] Response status:", xhr.status);
        console.log("[AUTH] Response text:", xhr.responseText);
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300 && data.user) {
            setUser(data.user);
            toast.success("Welcome back!", { description: `Signed in as ${data.user.name}` });
            navigate("/dashboard", { replace: true });
          } else {
            // Show more detailed error for debugging
            const errorMsg = data.detail || `Status ${xhr.status}: Unknown error`;
            console.error("[AUTH] Login failed:", errorMsg);
            toast.error("Access Denied", { description: errorMsg });
            navigate("/", { replace: true });
          }
        } catch (e) {
          console.error("[AUTH] Parse error:", e, "Response:", xhr.responseText);
          toast.error("Access Denied", { description: `Parse error: ${xhr.responseText?.substring(0, 100)}` });
          navigate("/", { replace: true });
        }
      };
      
      xhr.onerror = function() {
        console.error("[AUTH] Network error");
        toast.error("Access Denied", { description: "Network error during authentication" });
        navigate("/", { replace: true });
      };
      
      xhr.send(JSON.stringify({ session_id: sessionId }));
    } else {
      navigate("/", { replace: true });
    }
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout>
            <Dashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/roleplay/new" element={
        <ProtectedRoute>
          <Layout>
            <NewRoleplay />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/roleplay/active/:sessionId" element={
        <ProtectedRoute>
          <Layout hideNav>
            <ActiveRoleplay />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/roleplay/results/:sessionId" element={
        <ProtectedRoute>
          <Layout>
            <SessionResults />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/analyze" element={
        <ProtectedRoute>
          <Layout>
            <CallAnalysis />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/analyze/results/:analysisId" element={
        <ProtectedRoute>
          <Layout>
            <AnalysisResults />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/scripts" element={
        <ProtectedRoute>
          <Layout>
            <ScriptAnalysis />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/scripts/history" element={
        <ProtectedRoute>
          <Layout>
            <ScriptHistory />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/scripts/compare" element={
        <ProtectedRoute>
          <Layout>
            <ScriptCompare />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/scripts/:analysisId" element={
        <ProtectedRoute>
          <Layout>
            <ScriptResults />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/history" element={
        <ProtectedRoute>
          <Layout>
            <History />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/leaderboard" element={
        <ProtectedRoute>
          <Layout>
            <Leaderboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute>
          <Layout>
            <Analytics />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Layout>
            <Settings />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/team" element={
        <ProtectedRoute>
          <Layout>
            <Team />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/team/member/:memberId/session/:sessionId" element={
        <ProtectedRoute>
          <Layout>
            <TeamMemberSession />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/teams" element={
        <ProtectedRoute>
          <Layout>
            <TeamsAdmin />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/objection-handler" element={
        <ProtectedRoute>
          <Layout>
            <ObjectionHandler />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/embeds" element={
        <ProtectedRoute>
          <Layout>
            <Embeds />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/shared/:token" element={<SharedResults />} />
      <Route path="/shared/analysis/:token" element={<SharedCallAnalysis />} />
      {/* Embedded scenario routes - minimal UI, restricted access */}
      <Route path="/embed/:embedId" element={<EmbeddedScenario />} />
      <Route path="/embed/:embedId/active" element={
        <ProtectedRoute>
          <EmbeddedActiveRoleplay />
        </ProtectedRoute>
      } />
      <Route path="/embed/:embedId/results/:sessionId" element={
        <ProtectedRoute>
          <EmbeddedResults />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
